import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { MenuItem, TextField } from "@vapor/react-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDelegated } from "../../../core/api/use-get-delegated/useGetDelegated";
import { useUpdateDelegated } from "../../../core/api/use-update-delegated/useUpdateDelegated";
import { useSuccessNotification } from "../../../core/hooks/useSuccessNotification";
import {
    type Intermediary,
    IntermediaryType,
} from "../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";

interface DelegatedUpdaterProps {
    disabled: boolean;
    taxSubmissionId: string;
    intermediary: Intermediary;
    delegatedTaxId: string | null;
}

const NO_DELEGATED = "no_delegated";

export const DelegatedUpdater: React.FC<DelegatedUpdaterProps> = ({
    disabled,
    taxSubmissionId,
    intermediary,
    delegatedTaxId,
}) => {
    const [selectedDelegated, setSelectedDelegated] = useState<string>(
        delegatedTaxId
            ? delegatedTaxId
            : intermediary.type === IntermediaryType.PERSON
              ? NO_DELEGATED
              : "placeholder",
    );

    const { t } = useTranslation();
    const { showTextMessage: showSuccessMessage } = useSuccessNotification();
    const { isFetching: loadingDelegates, data: delegates } = useGetDelegated(
        intermediary.taxId,
    );
    const { isPending: updatingDelegated, mutateAsync: updateDelegated } =
        useUpdateDelegated();

    const handleDelegatedChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const delegatedTaxId =
            event.target.value === NO_DELEGATED ? null : event.target.value;

        try {
            await updateDelegated({ taxSubmissionId, delegatedTaxId });
            showSuccessMessage(t("components.delegated_updater.success"));
            setSelectedDelegated(event.target.value);
        } catch {
            // error toast already shown via meta.errorMessage in useUpdateDelegated
        }
    };

    const selectError =
        intermediary.type === IntermediaryType.COMPANY &&
        delegatedTaxId === null;

    return (
        <LoadingWrapper
            loading={loadingDelegates || updatingDelegated}
            spinnerProps={{ loadingText: "" }}
            variant="spinner"
        >
            {delegates && delegates.length > 0 && (
                <TextField
                    name="selectedDelegated"
                    onChange={handleDelegatedChange}
                    select
                    error={selectError}
                    disabled={disabled}
                    sx={{ minWidth: 180, textAlign: "left" }}
                    SelectProps={{
                        displayEmpty: true,
                    }}
                    value={selectedDelegated}
                >
                    {selectedDelegated === "placeholder" && (
                        <MenuItem
                            disabled
                            key="placeholder"
                            value="placeholder"
                        >
                            {t(
                                "components.delegated_updater.select.placeholder",
                            )}
                        </MenuItem>
                    )}
                    {intermediary.type === IntermediaryType.PERSON && (
                        <MenuItem value={NO_DELEGATED}>
                            {t(
                                "components.delegated_updater.select.no_delegated",
                            )}
                        </MenuItem>
                    )}
                    {delegates.map((delegate) => (
                        <MenuItem key={delegate.taxId} value={delegate.taxId}>
                            {delegate.firstName && delegate.lastName
                                ? `${delegate.firstName} ${delegate.lastName}`
                                : delegate.taxId}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {delegates && delegates.length === 0 && (
                <Typography variant="body">
                    {t(
                        "components.delegated_updater.select.no_available_delegated",
                    )}
                </Typography>
            )}
        </LoadingWrapper>
    );
};
